import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Tag,
  Space,
  Tooltip,
  Typography,
  Upload as UploadAntd,
  Avatar,
} from "antd";
import { UserOutlined, TeamOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import lang from "../helper/lang";
import CheckImgIcon from "../assets/images/activee-main-icon.svg";
import apiPath from "../constants/apiPath";
import moment, { period } from "moment";

const { Text } = Typography;
const { confirm } = Modal;

function ActiveSession() {
  const { request } = useRequest();

  // State management
  const [sessions, setSession] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Loading states for different operations
  const [deleteLoading, setDeleteLoading] = useState({});
  const [statusToggleLoading, setStatusToggleLoading] = useState({});

  const isInitialRender = useRef(true);

  // Fetch sessions
  const fetchSession = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    request({
      url: `/auth/admin/sessions?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let sessionData = [];
        let totalCount = 0;
        let currentPageFromApi = undefined;
        let limitFromApi = undefined;
        if (data?.data) {
          // Common shapes: { data: { sessions, pagination } } or { data: { docs, totalDocs } }
          if (Array.isArray(data.data)) {
            sessionData = data.data;
          }
          const meta = data.data.pagination || data.pagination || {};
          totalCount =
            meta.totalCount ?? meta.total ?? data.data.totalDocs ?? 0;
          currentPageFromApi = meta.currentPage ?? meta.page ?? meta.pageNumber;
          limitFromApi = meta.limit ?? meta.pageSize;
        } else if (Array.isArray(data)) {
          sessionData = data;
          totalCount = data.length;
        }

        setSession(Array.isArray(sessionData) ? sessionData : []);
        setPagination((prev) => ({
          ...prev,
          total: totalCount,
          current: currentPageFromApi ?? prev.current,
          pageSize: limitFromApi ?? prev.pageSize,
        }));
      },
      onError: (error) => {
        setLoading(false);
        setSession([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        console.log("Error occured while getting active session", error);
        // ShowToast("Failed to fetch sessions", Severty.ERROR);
      },
    });
  }, [pagination.current, pagination.pageSize, request]);

  useEffect(() => {
    fetchSession();
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    fetchSession();
  }, [pagination.current, pagination.pageSize]);

  // Handlers
  const handleTableChange = (paginationInfo, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };
  // --------------- Handle Terminate ------------
  const handleTerminateSession = async (user) => {
    try {
      const userId = user._id || user.id;
      request({
        url: `/auth/admin/sessions/${userId}/terminate`,
        method: "POST",
        onSuccess: (data) => {
          ShowToast(`User terminated successfully`, Severty.SUCCESS);
          fetchSession();
        },
        onError: (error) => {
          ShowToast(
            `${
              error.message ||
              error.response.data.message ||
              error.response.message ||
              "Failed to terminate session"
            }`,
            Severty.ERROR
          );
        },
      });
    } catch (error) {
      ShowToast(
        `${
          error.message ||
          error.response.data.message ||
          error.response.message ||
          "Failed to terminate session"
        }`,
        Severty.ERROR
      );
      const userId = user._id || user.id;
      setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: lang("User_ID") || "User ID",
      dataIndex: ["user", "id"],
      key: "userId",
      width: 120,
      render: (id) => <Text code>USER_{id}</Text>,
    },
    {
      title: lang("Name") || "Name",
      dataIndex: ["user", "full_name"],
      key: "name",
      width: 200,
      sorter: (a, b) => {
        const nameA = a?.user?.full_name?.toLowerCase() || "";
        const nameB = b?.user?.full_name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Avatar
            icon={<UserOutlined />}
            src={record?.user?.avatar || record?.user?.profileImage}
            style={{ backgroundColor: "#1890ff" }}
          />
          <span>{name || "N/A"}</span>
        </div>
      ),
    },
    {
      title: lang("Email") || "Email",
      dataIndex: "user_email",
      key: "user_email",
      width: 200,
      render: (email) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{email || "N/A"}</span>
        </div>
      ),
    },
    {
      title: lang("Last_Login") || "Last Login",
      dataIndex: "last_activity",
      key: "last_activity",
      width: 150,
      sorter: (a, b) =>
        moment(a.created_at).valueOf() -
        moment(b.created_at).valueOf(),
      render: (date) =>
        date ? moment(date).format("DD MMM YYYY, hh:mm A") : "-",
    },
    {
      title: lang("Status") || "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "In-Active"}
        </Tag>
      ),
    },
    {
      title: lang("Actions") || "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space size="small">
          {/* <Tooltip title={lang("View_Profile") || "View Profile"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewProfile(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          <Tooltip title={lang("Edit") || "Edit"}>
            <Button
              className="button-action-small"
              icon={<Pencil />}
              onClick={() => handleEditUser(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip> */}
          <Tooltip title={lang("Terminate") || "Terminate"}>
            <Button
              className="button-action-small"
              icon={<img src={CheckImgIcon} alt="terminate" />}
              style={{ color: "#52c41a" }}
              loading={statusToggleLoading[record.id]}
              disabled={
                statusToggleLoading[record.id] || deleteLoading[record.id]
              }
              onClick={() =>
                showSessionStatusConfirm(
                  record,
                  false,
                  handleTerminateSession,
                  statusToggleLoading,
                  deleteLoading
                )
              }
            />
          </Tooltip>
          {/* <Tooltip title={lang("Delete") || "Delete"}>
            <Button
              className="button-action-small"
              icon={<Trash2 />}
              style={{ color: "#ff4d4f" }}
              loading={deleteLoading[record._id || record.id]}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
              onClick={() => {
                setUserToDelete(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  // Terminate Session status confirm
  const showSessionStatusConfirm = (
    record,
    isActive,
    handleTerminateSession,

    statusToggleLoading,
    deleteLoading
  ) => {
    const loading = statusToggleLoading[record._id || record.id];
    const disabled = loading || deleteLoading[record._id || record.id];

    confirm({
      title: "Are you sure you want to terminate this user?",
      okText: "Yes",
      cancelText: "No",
      okType: "primary",
      centered: true,
      okButtonProps: {
        loading,
        disabled,
      },
      onOk() {
        handleTerminateSession(record);
      },
    });
  };

  //---------- Main Table -----------
  return (
    <div>
      <Card
        title={
          <>
            <TeamOutlined style={{ marginRight: 8 }} />
            {"Active Session"}
            <p>
              Admin manages end-user accounts: view, edit, terminate or delete
              as necessary.
            </p>
          </>
        }
      >
        {/* sessions Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={Array.isArray(sessions) ? sessions : []}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${lang("Showing") || "Showing"} ${range[0]}-${range[1]} ${
                  lang("of") || "of"
                } ${total} ${lang("sessions") || "sessions"}`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>
    </div>
  );
}

export default ActiveSession;
